import { useContext, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Logo from '../Components/Logo';
import NavbarLink from '../Components/Navbarlink';
import LangSwitcher from '../Components/LangSwitcher';
import { LanguageContext } from '../Components/LanguageContext';
import languages from '../lang/languages.json';

export default function NavigationBar({
  content,
  desktopNavBreakpoint = 'xl',
  logoClassName = '',
  b2bButtonClassName = '',
  hamburgerIconClassName = '',
}) {
  const [showingNavigationDropdown, setShowingNavigationDropdown] = useState(false);
  const [showingMobileLanguages, setShowingMobileLanguages] = useState(false);
  const { language, setLanguage, handleLangSwitch } = useContext(LanguageContext);
  const location = useLocation();
  const homeLabel = content?.homeLabel || '';
  const b2bLabel = content?.b2bLabel || '';
  const chooseLanguageLabel = content?.chooseLanguage || '';
  const closeMenuLabel = content?.closeMenu || '';
  const navItems = [
    content?.corporate,
    content?.support,
    content?.hr,
    content?.dealership,
    content?.contact,
  ].filter(Boolean);
  const desktopNavClassName =
    desktopNavBreakpoint === '1.5xl'
      ? 'my-6 hidden items-center justify-end gap-8 font-light 1.5xl:flex'
      : 'my-6 hidden items-center justify-end gap-8 font-light xl:flex';
  const desktopLangClassName =
    desktopNavBreakpoint === '1.5xl'
      ? 'hidden items-center justify-center 1.5xl:flex'
      : 'hidden items-center justify-center xl:flex';
  const mobileTriggerClassName =
    desktopNavBreakpoint === '1.5xl'
      ? 'flex items-center 1.5xl:hidden'
      : 'flex items-center xl:hidden';
  const mobilePanelVisibilityClassName =
    desktopNavBreakpoint === '1.5xl' ? '1.5xl:hidden' : 'xl:hidden';
  const mobileMenuItems = [
    { name: homeLabel, to: '/' },
    ...navItems,
  ];

  const closeMobileMenu = () => {
    setShowingNavigationDropdown(false);
    setShowingMobileLanguages(false);
  };

  const handleMobileLanguageSelect = (langCode) => {
    setLanguage(handleLangSwitch(langCode));
    closeMobileMenu();
  };

  useEffect(() => {
    if (!showingNavigationDropdown) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showingNavigationDropdown]);

  return (
    <div className="relative flex flex-1 justify-between">
      <div className="relative shrink-0 flex items-center" >
        <Link to='/' className=''>
          <Logo className={`w-auto fill-current text-gray-800 dark:text-gray-200 ${logoClassName}`} />
        </Link>
        {/* dark:invert dark:hue-rotate-[210deg] block */}
      </div>
      <div className='mt-8 flex items-center gap-4 1.5xl:gap-8'>
        <ul className={desktopNavClassName}>
          {navItems.map((item) => (
            <li
              key={item.to}
            >
              <NavbarLink to={item.to} >
                {item.name}
              </NavbarLink>
            </li>
          ))}
        </ul>
        <div className='hidden items-center justify-center 1.5md:flex'>
          <a href="https://b2b.bulutmarket.com.tr"
              className={`rounded-full px-5 py-2 bg-bg_secondary border border-white text-white text-lg
              font-semibold hover:bg-bg_quaternary transition-colors duration-200 ease-in-out ${b2bButtonClassName}`}
          >
              {b2bLabel}
          </a>
        </div>
        <div className={desktopLangClassName}>
          <LangSwitcher />
        </div>
        <div className={mobileTriggerClassName}>
          <button
              onClick={() => setShowingNavigationDropdown((previousState) => !previousState)}
              className="inline-flex items-center justify-center rounded-full text-white focus:outline-none hover:text-gray-300 max-[700px]:text-button_transition_1 max-[700px]:hover:text-orange-400 dark:focus:bg-gray-700/50 ">
            <svg className={`h-12 w-12 transition duration-150 ease-in-out ${hamburgerIconClassName}`} stroke="currentColor" fill="none" viewBox="0 0 24 24">
                <path
                    className={!showingNavigationDropdown ? 'inline-flex' : 'hidden'}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                />
                <path
                    className={showingNavigationDropdown ? 'inline-flex' : 'hidden'}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                />
            </svg>
          </button>
        </div>

      </div>

      {showingNavigationDropdown && (
        <div
          className={`fixed inset-0 z-[120] bg-white/18 backdrop-blur-[2px] ${mobilePanelVisibilityClassName}`}
          onClick={closeMobileMenu}
        >
          <div
            className="absolute inset-0 flex h-full flex-col overflow-y-auto bg-bg_secondary px-10 pb-12 pt-8 text-white shadow-[0_18px_38px_rgba(20,19,38,0.16)] max-[700px]:w-full min-[701px]:right-0 min-[701px]:left-auto min-[701px]:w-[24rem] min-[701px]:rounded-bl-[3.75rem] min-[1024px]:w-[26rem] min-[1280px]:w-[29rem] sm:px-12 sm:pb-14 sm:pt-9"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-12 flex justify-end">
              <button
                type="button"
                onClick={closeMobileMenu}
                className="text-white transition-colors duration-200 hover:text-gray-300"
                aria-label={closeMenuLabel}
              >
                <svg className="h-8 w-8 sm:h-9 sm:w-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <nav className="flex flex-col">
              {mobileMenuItems.map((item) => {
                const isActive = item.to === '/'
                  ? location.pathname === '/'
                  : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={closeMobileMenu}
                    className={`border-b border-[#cf7c3a] py-6 text-lg leading-none transition-colors duration-200 hover:text-text_tertiary sm:py-7 ${
                      isActive ? 'text-text_tertiary' : 'text-white'
                    }`}
                  >
                    {item.to === '/' ? homeLabel : item.name}
                  </Link>
                );
              })}
            </nav>

            <div className="mt-auto pt-14">
              <a
                href="https://b2b.bulutmarket.com.tr"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="inline-flex items-center justify-center rounded-full border border-white bg-button_transition_1 px-4 py-2 text-lg font-bold text-white transition-colors duration-200 hover:bg-button_transition_2"
              >
                {b2bLabel}
              </a>

              <div className="mt-14">
                <button
                  type="button"
                  onClick={() => setShowingMobileLanguages((previousState) => !previousState)}
                  className="flex items-center gap-2 text-lg text-white transition-colors duration-200 hover:text-gray-300"
                >
                  <span>{chooseLanguageLabel}</span>
                  <svg
                    className={`h-4 w-4 transition-transform duration-200 ${showingMobileLanguages ? 'rotate-90' : ''}`}
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M5.5 3.5 10 8l-4.5 4.5z" />
                  </svg>
                </button>

                {showingMobileLanguages && (
                  <div className="mt-4 space-y-3">
                    {Object.values(languages).map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => handleMobileLanguageSelect(lang.code)}
                        className={`block text-left text-base transition-colors duration-200 hover:text-text_primary ${
                          language === lang.code ? 'text-text_primary' : 'text-white/90'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
